import React, { useContext } from 'react'
import { useIntl } from 'react-intl'

import { Card, Col, Form, Input, Row, notification } from 'antd'
import { LoginIcon } from 'components/icons'
import { AppContext } from 'core'
import FormComponent from 'core/components/FormComponent'
import * as R from 'ramda'

export default function LoginPage() {
  const { getRoute } = useContext(AppContext)
  const intl = useIntl()

  const recoverPasswordRoute = getRoute('new_user_password')

  /**
   * @function onLoginSuccess
   */
  const onLoginSuccess = ({ payload }) => {
    const loginRedirectRoute = getRoute('root')
    storeVidimKeys(payload)
    window.location.href = loginRedirectRoute.path
  }

  const onLoginError = (response) => {
    console.log('ERRORS', response.errors)
    if (!response) {
      notification.error({
        message: intl.formatMessage({ id: 'generic.error' })
      })
      return
    }
    if (Array.isArray(response.errors))
      for (let i = 0; i < response.errors.length; i++) {
        const element = response.errors[i]
        console.log('ERRORS', element)
        notification.error({ message: element.message, duration: 5 })
      }
    else {
      for (const errorKey in response.errors) {
        notification.error({ message: response.errors[errorKey], duration: 5 })
      }

      if (Object.keys(response.errors).length === 0)
        notification.error({
          message: intl.formatMessage({ id: 'generic.error' })
        })
    }
  }

  const storeVidimKeys = (payload) => {
    const inplant = {
      auth: {
        error: null,
        fetching: false,
        loggedIn: true,
        user: {
          roles: [],
          forbiddenActions: [],
          branding: {
            uiTheme: 'default'
          }
        },
        mobile: false
      },
      components: {
        tableReducer: {
          pagination: {},
          listFilters: {},
          columnSize: {}
        }
      },
      tutelage: {
        pageConfig: {}
      },
      core: {
        brandingManager: {
          assets: {
            favicon: '#favicon#',
            logoLogin: '#logoLogin#',
            logoTopBar: '#logoTopbar#',
            slug: '#slug#'
          },
          status: 'success',
          theme: {
            themeFile: '#themeFile#',
            themeName: '#themeName#',
            themeType: 'light'
          }
        },
        settingsManager: { isMenuCollapsed: false }
      },
      config: {
        generic: {
          loginSuccessPath: '/maintenances/my-task-list',
          useNewLogin: true,
          version: '2.1.0-dti-cloud-1.4.0',
          externalPasswordRecovery: {
            link: 'https://dcloud.dtindustry.com/machines/panel/authentication/recover-password-view'
          }
        }
      }
    }

    const vidimResponse = R.prop('vidim_response', payload)

    localStorage.setItem('vidim_response', JSON.stringify(vidimResponse))

    const formatNavAndViewAclRules = (rules) => {
      if (!rules) return []
      for (let i = 0; i < rules.length; i++) {
        const rule = rules[i]
        if (R.startsWith('nav.', rule) || R.startsWith('view.', rule)) {
          rules[i] = R.pipe(
            R.replace('nav.', 'nav./'),
            R.replace('view.', 'view./'),
            R.replace(/_/g, '/')
          )(rule)
        }
      }
      return rules
    }

    const aclRulesTransform = (keys) => {
      return R.zipObj(keys, R.repeat(true, keys.length))
    }

    const aclRulesPipe = R.pipe(formatNavAndViewAclRules, aclRulesTransform)

    const enrichedInplant = R.pipe(
      R.over(
        R.lensPath(['auth', 'user']),
        R.mergeDeepRight({
          ...R.pick(
            ['aclRules', 'uuid', 'username', 'displayName', 'uiTheme'],
            vidimResponse
          ),
          username: R.prop('userName', vidimResponse),
          displayName: R.props(['firstName', 'lastName'], vidimResponse).join(
            ' '
          ),
          aclRules: aclRulesPipe(R.prop('aclRules', vidimResponse))
        })
      )
    )(inplant)

    localStorage.setItem(
      'enriched_inplant_log',
      JSON.stringify(enrichedInplant)
    )
    localStorage.setItem('jwt', R.prop('token', vidimResponse))

    const replaceObj = vidimResponse.uiTheme != 'default' ? {
      '#favicon#': `favicon-${vidimResponse.uiTheme}.png`,
      '#logoLogin#': `logo-login-${vidimResponse.uiTheme}.png`,
      '#logoTopbar#': `logo-${vidimResponse.uiTheme}.png`,
      '#slug#': `${vidimResponse.uiTheme}`,
      '#themeFile#': `index-${vidimResponse.uiTheme}-light.css`,
      '#themeName#': `${vidimResponse.uiTheme}`,
    } : {
      '#favicon#': 'favicon.ico',
      '#logoLogin#': 'logo-login.png',
      '#logoTopbar#': 'logo.png',
      '#slug#': 'default',
      '#themeFile#': 'index-default-light.css',
      '#themeName#': 'default'
    }

    const re = new RegExp(Object.keys(replaceObj).join('|'), 'gi')

    localStorage.setItem(
      'persist:inplant',
      JSON.stringify(R.map(JSON.stringify, enrichedInplant)).replace(re, (matched) => {
        return replaceObj[matched]
      })
    )
  }

  return (
    <Row justify='center' align='center'>
      <Col style={{ maxWidth: 500, width: '100%' }}>
        <Card cover={<LoginIcon />}>
          <FormComponent
            path='user_session'
            onResponseSuccess={onLoginSuccess}
            onResponseError={onLoginError}
            confirmLabel='login.submit'
            confirmBlock
            submitProps={{ size: 'large', style: { marginTop: '15px' } }}
          >
            <Form.Item
              name={['user', 'username']}
              label={intl.formatMessage({
                id: 'login.username_or_email.translation'
              })}
              rules={[{ required: true }]}
            >
              <Input
                size='large'
                autoComplete='username'
                placeholder={intl.formatMessage({
                  id: 'login.username_or_email.placeholder'
                })}
              />
            </Form.Item>

            <Form.Item
              name={['user', 'password']}
              label={intl.formatMessage({ id: 'login.password.translation' })}
              rules={[{ required: true }]}
            >
              <Input.Password
                size='large'
                autoComplete='current-password'
                placeholder={intl.formatMessage({
                  id: 'login.password.placeholder'
                })}
              />
            </Form.Item>
          </FormComponent>

          <div style={{ textAlign: 'right', marginTop: 15 }}>
            <a href={recoverPasswordRoute.path}>
              {intl.formatMessage({ id: 'cta.recover_password' })}
            </a>
          </div>
        </Card>
      </Col>
    </Row>
  )
}
